import { reactive } from "vue";
import { decodeJwt, rolesFromPayload } from "../jwt.js";
const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
export class GoogleGisProvider {
    name = "google";
    state;
    clientId;
    storageKey;
    hostedDomain;
    scriptLoaded = null;
    gsiConfigured = false;
    constructor(opts) {
        if (!opts.clientId) {
            throw new Error("GoogleGisProvider: clientId is required");
        }
        this.clientId = opts.clientId;
        this.storageKey = opts.storageKey;
        this.hostedDomain = opts.hostedDomain;
        this.state = reactive({
            status: "idle",
            user: null,
            idToken: null,
            accessToken: null,
            error: null,
        });
    }
    async init() {
        this.state.status = "initializing";
        const credential = sessionStorage.getItem(this.storageKey);
        if (credential) {
            const user = this.userFromCredential(credential);
            if (user) {
                this.state.user = user;
                this.state.idToken = credential;
                this.state.accessToken = null;
                this.state.status = "authenticated";
                return;
            }
            sessionStorage.removeItem(this.storageKey);
        }
        await this.loadScript();
        this.configureGsi();
        this.state.status = "unauthenticated";
    }
    async login() {
        await this.loadScript();
        this.configureGsi();
        window.google.accounts.id.prompt();
    }
    async logout() {
        sessionStorage.removeItem(this.storageKey);
        if (window.google?.accounts.id) {
            window.google.accounts.id.disableAutoSelect();
        }
        this.state.user = null;
        this.state.idToken = null;
        this.state.accessToken = null;
        this.state.status = "unauthenticated";
    }
    loadScript() {
        if (this.scriptLoaded)
            return this.scriptLoaded;
        this.scriptLoaded = new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`);
            if (existing) {
                if (window.google?.accounts.id) {
                    resolve();
                    return;
                }
                existing.addEventListener("load", () => resolve());
                existing.addEventListener("error", () => reject(new Error("Failed to load Google Identity Services")));
                return;
            }
            const script = document.createElement("script");
            script.src = GIS_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
            document.head.appendChild(script);
        });
        return this.scriptLoaded;
    }
    configureGsi() {
        if (this.gsiConfigured)
            return;
        if (!window.google?.accounts.id) {
            throw new Error("Google Identity Services not available");
        }
        window.google.accounts.id.initialize({
            client_id: this.clientId,
            callback: (response) => this.onCredential(response.credential),
            hosted_domain: this.hostedDomain,
            auto_select: false,
            use_fedcm_for_prompt: true,
        });
        this.gsiConfigured = true;
    }
    onCredential(credential) {
        const user = this.userFromCredential(credential);
        if (!user) {
            this.state.status = "error";
            this.state.idToken = null;
            this.state.accessToken = null;
            this.state.error = this.hostedDomain
                ? `Login muss mit einem ${this.hostedDomain}-Account erfolgen.`
                : "Ungueltiges ID-Token.";
            return;
        }
        sessionStorage.setItem(this.storageKey, credential);
        this.state.user = user;
        this.state.idToken = credential;
        this.state.accessToken = null;
        this.state.status = "authenticated";
        this.state.error = null;
    }
    userFromCredential(credential) {
        const payload = decodeJwt(credential);
        if (!payload)
            return null;
        if (payload.exp * 1000 <= Date.now())
            return null;
        if (payload.aud !== this.clientId)
            return null;
        if (payload.iss !== "https://accounts.google.com" && payload.iss !== "accounts.google.com")
            return null;
        if (this.hostedDomain && payload.hd !== this.hostedDomain)
            return null;
        return {
            id: payload.sub,
            email: payload.email,
            name: payload.name,
            picture: payload.picture,
            hostedDomain: payload.hd,
            roles: rolesFromPayload(payload),
        };
    }
}
//# sourceMappingURL=google-gis.js.map